import { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import {
  ChipsSeleccion,
  Icon,
  Stepper,
  TarjetasOpcion,
  type OpcionTarjeta,
} from "@/components/ds";
import { capacitacionesIaApi } from "@/lib/api/capacitaciones-ia";
import { apiCapacitacionesDocumentosAdapter } from "@/lib/capacitaciones-documentos-adapter";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import type {
  CoberturaPublicoObjetivoTipo,
  DestinatariosOpciones,
  Programa,
  ProgramaNivel,
} from "@/types/capacitaciones-ia";
import type { EmpresaDocumentoTipo } from "@/types/empresa-documento-tipo";
import { toast } from "sonner";

/** 50 MB — mismo tope que `ArchivoAdhocWriter::MAX_KB` en fc-api (51200 KB).
 * No hay tope en la CANTIDAD de fuentes — el usuario decidió explícitamente
 * que 1, 12 o más fuentes son igual de válidas; sólo el tamaño por archivo
 * sigue acotado (subió de 12 MB a 50 MB, no se retiró). */
const ADHOC_MAX_BYTES = 50 * 1024 * 1024;
/** Misma familia de tipos que `ArchivoAdhocWriter::mimeTypesPermitidos()` —
 * la validación real es server-derivada (sniffea el contenido), esto es sólo
 * para dar feedback inmediato sin gastar un round-trip. */
const ADHOC_ACCEPT_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg",
  "image/png",
];
const ADHOC_ACCEPT_EXT = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".jpg", ".jpeg", ".png"];

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Tres opciones excluyentes de una escala conocida: eso es un selector, no
 *  tres tarjetas de ancho completo. La descripción no se pierde — pasa a ser
 *  la línea de ayuda del nivel elegido, debajo del control. */
const NIVELES: { valor: ProgramaNivel; titulo: string; descripcion: string }[] = [
  { valor: "basico", titulo: "Básico", descripcion: "Lenguaje simple, foco en qué hacer" },
  { valor: "intermedio", titulo: "Intermedio", descripcion: "Incluye normativa y casos de faena" },
  { valor: "avanzado", titulo: "Avanzado", descripcion: "Detalle técnico y criterios de decisión" },
];

/** Mismo rango que traía el slider (3–24). */
const CANTIDADES_TEMATICAS = Array.from({ length: 22 }, (_, i) => i + 3);

interface FuenteOpcion {
  id: number;
  label: string;
}

export interface NuevoProgramaDrawerProps {
  abierto: boolean;
  onCerrar: () => void;
  /** Se llama tras crear el Programa — el padre refresca la lista para que la fila "generando" aparezca. */
  onCreado: (programaId: number) => void;
  /**
   * El Programa recién creado, tal como lo ve la lista. El padre ya refresca
   * solo mientras la IA genera (`usePollSolicitud`), así que el drawer LEE ese
   * estado en vez de abrir un segundo polling propio: dos fuentes para el
   * mismo hecho terminan contradiciéndose en pantalla. `null` mientras el
   * refresco todavía no lo trajo.
   */
  programaCreado: Programa | null;
  /**
   * `false` cuando el refresco automático del padre ya se agotó. Sin esto el
   * spinner seguiría prometiendo una actualización en vivo que nunca va a
   * llegar — que es exactamente lo que hay que evitar acá.
   */
  autoRefresco: boolean;
}

/**
 * Drawer "Nuevo programa" (D11, 620px, una columna). Adaptado al flujo REAL
 * de backend (design #1399, C5a/C5b): a diferencia del prototipo —que simula
 * la generación completa en el cliente y deja revisar/editar un borrador
 * ANTES de un "Guardar programa" separado— acá `POST /programas` YA CREA el
 * Programa (visible como "generando" en la lista) apenas se confirma. No
 * existe un paso intermedio de "borrador sin persistir": por eso hay un solo
 * CTA (no dos).
 *
 * Ya no hay una columna derecha dedicada a mostrar el estado ("Todavía no
 * hay temáticas" / generando / listo / fallido) — ocupaba dos tercios del
 * ancho para no mostrar más que texto. Ese estado sigue siendo reactivo
 * (sigue el Programa real, `generando` → `listo` / `fallido`, con el mismo
 * dato que la tabla), pero ahora vive en el pie: un ícono + el texto del
 * hint que ya existía, sin agregar una segunda superficie.
 *
 * W2b (sdd/nuevo-programa-multifuente, design #1663 D5/D8/D11;
 * quitar-flags-multifuente retiró el kill switch una vez verificado en vivo
 * con api-pfp-app-service): "Documentos fuente" es siempre un `MultiSelect`
 * (documentos de la empresa) + una zona de arrastrar-y-soltar para archivos
 * ad-hoc que sólo alimentan este Programa.
 */
export function NuevoProgramaDrawer({
  abierto,
  onCerrar,
  onCreado,
  programaCreado,
  autoRefresco,
}: NuevoProgramaDrawerProps) {
  const [fuentes, setFuentes] = useState<FuenteOpcion[]>([]);
  const [cargandoFuentes, setCargandoFuentes] = useState(false);
  const [opciones, setOpciones] = useState<DestinatariosOpciones | null>(null);

  const [documentoIds, setDocumentoIds] = useState<string[]>([]);
  const [archivosAdhoc, setArchivosAdhoc] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputAdhocRef = useRef<HTMLInputElement>(null);

  const [nombre, setNombre] = useState("");
  const [cantidadTematicas, setCantidadTematicas] = useState(10);
  const [nivel, setNivel] = useState<ProgramaNivel>("intermedio");
  const [preguntasPorTematica, setPreguntasPorTematica] = useState(5);
  // «Toda la empresa» por defecto: es el caso más común y el mismo default
  // que ya usa "Programar envíos". El servidor NO lo pone — si el formulario
  // no lo mandara, el Programa nacería sin declarar como hasta ahora.
  const [publicoTipo, setPublicoTipo] = useState<CoberturaPublicoObjetivoTipo>("empresa");
  const [publicoRefs, setPublicoRefs] = useState<number[]>([]);

  const [creando, setCreando] = useState(false);
  const [creado, setCreado] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    // Reset del formulario cada vez que se abre — evita arrastrar la
    // selección de una creación anterior en la misma sesión.
    setDocumentoIds([]);
    setArchivosAdhoc([]);
    setNombre("");
    setCantidadTematicas(10);
    setNivel("intermedio");
    setPreguntasPorTematica(5);
    setPublicoTipo("empresa");
    setPublicoRefs([]);
    setCreado(false);

    let cancelado = false;
    setCargandoFuentes(true);
    (async () => {
      try {
        const [tipos, docs] = await Promise.all([
          apiCapacitacionesDocumentosAdapter.listTipos(),
          apiCapacitacionesDocumentosAdapter.listDocumentos(),
        ]);
        if (cancelado) return;
        const labelPorTipo = new Map<number, string>(
          tipos.map((t: EmpresaDocumentoTipo) => [t.id, t.label])
        );
        setFuentes(
          docs
            .filter((d) => d.usar_para_capacitaciones)
            .map((d) => ({
              id: d.id,
              label: labelPorTipo.get(d.tipo_documento_id) ?? d.filename_original,
            }))
        );
      } catch {
        if (!cancelado) setFuentes([]);
      } finally {
        if (!cancelado) setCargandoFuentes(false);
      }
    })();

    // Catálogo de cargos/áreas para el público objetivo — el MISMO padrón que
    // alimenta "Programar envíos" y el editor en línea de la fila expandida.
    // Va en su propio bloque, no dentro del Promise.all de arriba: si este
    // falla, el drawer todavía sirve para crear con «Toda la empresa», que no
    // necesita catálogo. Metido en el all, un 500 acá dejaría la lista de
    // documentos vacía y el drawer inservible.
    (async () => {
      try {
        const res = await capacitacionesIaApi.getDestinatariosOpciones();
        if (!cancelado) setOpciones(res);
      } catch {
        if (!cancelado) setOpciones(null);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [abierto]);

  /** Cuántas fuentes (documentos + ad-hoc) hay elegidas ahora mismo. */
  const totalFuentes = documentoIds.length + archivosAdhoc.length;

  function onDocumentoIdsChange(next: string[]) {
    // Sugiere el nombre sólo cuando esta es la primera fuente que se elige y
    // el campo sigue vacío.
    if (nombre.trim() === "" && archivosAdhoc.length === 0 && next.length === 1) {
      const elegido = fuentes.find((f) => String(f.id) === next[0]);
      if (elegido) setNombre(elegido.label);
    }
    setDocumentoIds(next);
  }

  function quitarArchivoAdhoc(index: number) {
    setArchivosAdhoc((prev) => prev.filter((_, i) => i !== index));
  }

  /** Valida tamaño y tipo EN EL NAVEGADOR, antes de que salga cualquier
   * request — un archivo que sólo falla en el servidor gasta una subida y
   * devuelve un 422 confuso (design #1663 D8/D9). */
  function agregarArchivosAdhoc(archivos: File[]) {
    if (archivos.length === 0) return;

    const aceptados: File[] = [];
    for (const archivo of archivos) {
      if (archivo.size > ADHOC_MAX_BYTES) {
        toast.error(`"${archivo.name}" supera los 50 MB permitidos.`);
        continue;
      }
      const ext = archivo.name.slice(archivo.name.lastIndexOf(".")).toLowerCase();
      if (!ADHOC_ACCEPT_MIME.includes(archivo.type) && !ADHOC_ACCEPT_EXT.includes(ext)) {
        toast.error(`"${archivo.name}" no es un tipo permitido. Sólo PDF, Word, Excel o imagen.`);
        continue;
      }
      aceptados.push(archivo);
    }
    if (aceptados.length > 0) setArchivosAdhoc((prev) => prev.concat(aceptados));
  }

  function onInputChangeAdhoc(e: React.ChangeEvent<HTMLInputElement>) {
    const archivos = Array.from(e.target.files ?? []);
    // Permite volver a elegir el mismo archivo tras un rechazo (reset del input).
    e.target.value = "";
    agregarArchivosAdhoc(archivos);
  }

  function onDropAdhoc(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (creado) return;
    agregarArchivosAdhoc(Array.from(e.dataTransfer.files ?? []));
  }

  // Mismo guard que `refsIncompletas` en la fila expandida: elegir cargo o
  // área sin marcar ninguno no es una selección válida.
  const publicoIncompleto =
    (publicoTipo === "cargo" || publicoTipo === "area") && publicoRefs.length === 0;
  const textoPublicoIncompleto =
    publicoTipo === "cargo" ? "Elige al menos un cargo." : "Elige al menos un área.";

  async function generar() {
    if (creando) return;
    if (totalFuentes === 0) {
      toast.error("Elige al menos un documento o sube un archivo.");
      return;
    }
    if (publicoIncompleto) {
      toast.error(textoPublicoIncompleto);
      return;
    }

    setCreando(true);
    try {
      const res = await capacitacionesIaApi.crearPrograma({
        empresa_documento_ids: documentoIds.map(Number),
        archivos_adhoc: archivosAdhoc,
        nombre: nombre.trim() === "" ? undefined : nombre.trim(),
        nivel,
        cantidad_tematicas: cantidadTematicas,
        preguntas_por_tematica: preguntasPorTematica,
        publico_objetivo_tipo: publicoTipo,
        publico_objetivo_refs: publicoTipo === "empresa" ? [] : publicoRefs,
      });
      setCreado(true);
      onCreado(res.programa.id);
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.message
          ? err.message
          : "No pudimos comunicarnos con el servicio de generación. Intenta de nuevo en unos minutos."
      );
    } finally {
      setCreando(false);
    }
  }

  const mesesEstimados = Math.max(1, Math.round(cantidadTematicas / 4.3));
  const descripcionNivel = NIVELES.find((n) => n.valor === nivel)?.descripcion ?? "";

  // El copy describe EL PROGRAMA, no la audiencia ("este programa es
  // general"), y por eso no lleva párrafo de ayuda: cualquier frase que
  // arranque nombrando personas ("a quiénes les llega", "dirigido a") se lee
  // como una orden de envío, y después hay que gastar tres líneas aclarando
  // que no lo es (!524). El verbo `ser` hace ese trabajo solo.
  //
  // «Por área» sólo si la empresa declaró áreas, mismo criterio que el editor
  // en línea (las áreas son un clasificador opcional, no un restrictor).
  const tarjetasPublico: OpcionTarjeta<CoberturaPublicoObjetivoTipo>[] = [
    { valor: "empresa", titulo: "General", descripcion: "Para cualquier trabajador", icono: "domain" },
    { valor: "cargo", titulo: "Por cargo", descripcion: "Sólo los cargos que elijas", icono: "badge" },
    ...(opciones && opciones.areas.length > 0
      ? ([
          { valor: "area", titulo: "Por área", descripcion: "Sólo las áreas que elijas", icono: "location_on" },
        ] as OpcionTarjeta<CoberturaPublicoObjetivoTipo>[])
      : []),
  ];

  // Estado real del Programa recién creado. Mientras el refresco del padre no
  // lo haya traído (`null`), se asume `generando`: es lo que `POST /programas`
  // acaba de devolver.
  const estado = programaCreado?.estado ?? "generando";
  const generando = estado === "generando";
  const fallido = estado === "fallido";
  const tematicasGeneradas = programaCreado?.temas.length ?? 0;
  /** Sigue generando pero ya nadie está consultando: el spinner mentiría. */
  const sinNoticias = generando && !autoRefresco;

  // Ícono + color del hint del pie cuando ya existe un Programa — mismo
  // vocabulario visual que tenía el panel derecho eliminado (D11), ahora
  // como una sola línea en vez de una segunda superficie.
  const estadoIcono = fallido
    ? "error"
    : !generando
      ? "check_circle"
      : sinNoticias
        ? "schedule"
        : "progress_activity";
  const estadoIconoClase = cn(
    "mt-px shrink-0",
    fallido && "text-destructive",
    !fallido && !generando && "text-success",
    generando && sinNoticias && "text-icon-soft",
    generando && !sinNoticias && "text-primary-hover animate-fc-spin"
  );

  return (
    <Sheet open={abierto} onOpenChange={(v) => !v && onCerrar()}>
      <SheetContent className="sm:w-[620px] sm:max-w-[96vw]">
        <SheetHeader>
          <SheetTitle>Nuevo programa de capacitación</SheetTitle>
          <SheetDescription>
            La IA propone las temáticas; tú las editas o las deshaces antes de que salgan.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
          <div>
            <Label className="mb-1.5 text-[12.5px]">Documentos fuente</Label>
            <MultiSelect
              options={fuentes.map((f) => ({ value: String(f.id), label: f.label }))}
              value={documentoIds}
              onChange={onDocumentoIdsChange}
              placeholder={cargandoFuentes ? "Cargando…" : "Elige documentos"}
              searchPlaceholder="Buscar un documento…"
              emptyText="Ningún documento coincide con la búsqueda."
              disabled={creado}
            />
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <p className="text-soft text-[11px]">
                {fuentes.length} documentos marcados como fuente en Documentos Empresa
              </p>
              {documentoIds.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className="h-5 px-1.5 text-[11px]"
                  onClick={() => setDocumentoIds([])}
                  disabled={creado}
                >
                  Quitar todos
                </Button>
              )}
            </div>

            {archivosAdhoc.length > 0 && (
              <div className="mt-2 flex flex-col gap-1.5">
                {archivosAdhoc.map((archivo, i) => (
                  <div
                    key={`${archivo.name}-${i}`}
                    className="border-primary-border bg-primary-50 flex items-center gap-2.5 rounded-lg border px-2.5 py-2"
                  >
                    <Icon name="upload_file" size={16} className="text-primary-hover shrink-0" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-[12px]">{archivo.name}</span>
                      <span className="text-soft text-[10.5px]">
                        {formatBytes(archivo.size)} · sólo para este programa
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => quitarArchivoAdhoc(i)}
                      disabled={creado}
                      aria-label={`Quitar ${archivo.name}`}
                      className="text-primary-hover hover:bg-primary-100 shrink-0 rounded-md p-1"
                    >
                      <Icon name="close" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={inputAdhocRef}
              type="file"
              multiple
              accept={ADHOC_ACCEPT_EXT.join(",")}
              className="hidden"
              onChange={onInputChangeAdhoc}
              disabled={creado}
            />
            <button
              type="button"
              onClick={() => !creado && inputAdhocRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                if (!creado) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDropAdhoc}
              disabled={creado}
              className={cn(
                "border-border-strong bg-surface-muted mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-[12px] font-medium text-muted-foreground transition-colors",
                "hover:border-primary/60 hover:bg-primary-50",
                dragging && "border-primary bg-primary-50",
                creado && "pointer-events-none opacity-60"
              )}
            >
              <Icon name="upload_file" size={16} className="text-icon-soft shrink-0" />
              Sube un documento o arrástralo aquí
            </button>
            <p className="text-soft mt-1.5 text-[11px]">
              Lo que subas se usa sólo en este programa. PDF, Word, Excel o imagen, hasta 50 MB
              por archivo — sin límite en la cantidad.
            </p>
          </div>

          <div>
            <Label htmlFor="nuevo-programa-nombre" className="mb-1.5 text-[12.5px]">
              Nombre del programa
            </Label>
            <Input
              id="nuevo-programa-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={creado}
            />
          </div>

          {/* Va acá, no al final: documento, nombre y clasificación son lo
              que el programa ES; cantidad, nivel y preguntas son parámetros
              de cómo la IA lo genera. Fila de tres (D11) — antes tres
              tarjetas apiladas de ancho completo para textos de cuatro
              palabras; ahora ocupan el ancho del drawer en vez de la altura.
              Apilada por debajo de `sm`, como el resto del drawer. */}
          <div>
            <Label className="mb-1.5 text-[12.5px]">Este programa es</Label>
            <TarjetasOpcion<CoberturaPublicoObjetivoTipo>
              opciones={tarjetasPublico}
              valor={publicoTipo}
              onCambiar={(v) => {
                setPublicoTipo(v);
                setPublicoRefs([]);
              }}
              columnas={3}
              disabled={creado}
            />
            {publicoTipo === "cargo" && opciones && opciones.cargos.length > 0 && (
              <ChipsSeleccion
                className="mt-2"
                opciones={opciones.cargos.map((c) => ({ id: c.id, etiqueta: c.nombre }))}
                seleccionados={publicoRefs}
                onCambiar={setPublicoRefs}
                disabled={creado}
                aria-label="Cargos de este programa"
              />
            )}
            {publicoTipo === "area" && opciones && opciones.areas.length > 0 && (
              <ChipsSeleccion
                className="mt-2"
                opciones={opciones.areas.map((a) => ({ id: a.id, etiqueta: a.nombre }))}
                seleccionados={publicoRefs}
                onCambiar={setPublicoRefs}
                disabled={creado}
                aria-label="Áreas de este programa"
              />
            )}
          </div>

          {/* Los tres parámetros de generación en una sola fila: son la misma
              decisión ("cuánto y qué tan hondo") y se eligen mirando los tres
              juntos. Antes Nivel ocupaba una fila propia de tres tarjetas y
              Temáticas un slider aparte, así que la respuesta estaba repartida
              en tres alturas distintas del drawer. La línea de ayuda de
              Temáticas y Nivel es la misma que ya mostraban el slider y las
              tarjetas — el rediseño no agrega texto, lo reubica. Apilada por
              debajo de `sm`, como el resto del drawer. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label htmlFor="nuevo-programa-tematicas" className="mb-1.5 text-[12.5px]">
                Temáticas
              </Label>
              <NativeSelect
                id="nuevo-programa-tematicas"
                wrapperClassName="w-full"
                value={String(cantidadTematicas)}
                onChange={(e) => setCantidadTematicas(Number(e.target.value))}
                disabled={creado}
              >
                {CANTIDADES_TEMATICAS.map((n) => (
                  <NativeSelectOption key={n} value={String(n)}>
                    {n}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <p className="text-soft mt-1.5 text-[11px]">
                ≈ {mesesEstimados} {mesesEstimados === 1 ? "mes" : "meses"} a una por semana
              </p>
            </div>

            <div>
              <Label htmlFor="nuevo-programa-nivel" className="mb-1.5 text-[12.5px]">
                Nivel
              </Label>
              <NativeSelect
                id="nuevo-programa-nivel"
                wrapperClassName="w-full"
                value={nivel}
                onChange={(e) => setNivel(e.target.value as ProgramaNivel)}
                disabled={creado}
              >
                {NIVELES.map((n) => (
                  <NativeSelectOption key={n.valor} value={n.valor}>
                    {n.titulo}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <p className="text-soft mt-1.5 text-[11px]">{descripcionNivel}</p>
            </div>

            <div>
              <Label className="mb-1.5 text-[12.5px]">Preguntas por temática</Label>
              <Stepper
                valor={preguntasPorTematica}
                onCambiar={setPreguntasPorTematica}
                min={2}
                max={15}
                className="h-9 w-full rounded-lg"
                disabled={creado}
                aria-label="Preguntas por temática"
              />
            </div>
          </div>

          <div className="bg-primary-50 border-primary-border flex gap-2.5 rounded-lg border p-3">
            <Icon name="savings" size={17} className="text-primary-hover shrink-0" />
            <p className="text-[11.5px] leading-[1.5] text-muted-foreground">
              Generar las temáticas te cuesta{" "}
              <strong className="font-semibold">0,2 créditos</strong>. El cuestionario de cada
              una se genera con IA recién <strong className="font-semibold">el día del envío</strong>,
              no ahora — por eso armar un programa te sale barato.
            </p>
          </div>
        </div>

        <SheetFooter>
          <div className="flex flex-1 items-start gap-2">
            {creado && <Icon name={estadoIcono} size={16} className={estadoIconoClase} />}
            <span className="text-soft text-xs">
              {!creado
                ? totalFuentes === 0
                  ? "Elige al menos un documento o sube un archivo."
                  : publicoIncompleto
                    ? textoPublicoIncompleto
                    : "Todo listo para generar."
                : fallido
                  ? `${programaCreado?.falla_motivo ?? "La generación no llegó a completarse."} El documento quedó libre: puedes volver a intentarlo desde «Nuevo programa».`
                  : !generando
                    ? tematicasGeneradas > 0
                      ? `Listo — ${tematicasGeneradas} temáticas ya están en la lista.`
                      : "Listo — las temáticas ya están en la lista."
                    : sinNoticias
                      ? "Dejamos de consultar por ahora para no sobrecargar la pestaña. El programa sigue generándose: cierra esta ventana y revisa su estado en la lista."
                      : "Puedes cerrar esta ventana: la generación sigue en segundo plano."}
            </span>
          </div>
          {creado ? (
            <Button size="sm" onClick={onCerrar}>
              {!generando && !fallido ? "Ver temáticas" : "Cerrar"}
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={onCerrar} disabled={creando}>
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={generar}
                disabled={creando}
                className={cn(totalFuentes === 0 && "bg-muted text-soft shadow-none hover:bg-muted")}
              >
                <Icon name="auto_awesome" size={16} />
                {creando ? "Generando…" : "Generar programa"}
              </Button>
            </>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
